import { memo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Upload } from 'lucide-react'

import { Button } from '../../ui/button'
import TitleInputAutoCompletion from '../../analytics-details-v2/view/readership-profile/inputs/titles/TitleInputAutoCompletion'
import { BookSearchInterface } from '../../../core/interface/book-search.interface'
import { BookInterface } from '../../../core/interface/book.interface'
import { SetColorConfig } from '../../../core/config/SetColorConfig'
import ImportAudienceModal from './ImportAudienceModal'
import ImportedAudienceTable from './ImportedAudienceTable'
import {
  ChroniqueurResponseInterface,
  CreateChroniqueurResponse,
  InfluencerBody,
} from '../../../core/interface/chronique.interface'
import PostChroniqueurPreview from '../../../core/api/services/bookmetrie/private-reports/chroniqueur/post-chroniqueur-preview.service'
import WorkMirrorBookDetailsCard from '../../analytics-details-v2/view/work-mirror/WorkMirrorBookDetailsCard'
import useShowWorkProfileHook from '../../../core/hooks/useShowWorkProfile.hook'
import PostChroniqueur from '../../../core/api/services/bookmetrie/private-reports/chroniqueur/post-chroniqueur.service'
import ChroniquerProgressBar from '../shared/ChroniquerProgressBar'

interface Props {
  title: string
  onValidatePreSelectedAudience: () => void
  booksSelected: BookInterface[]
  setBooksSelected: (books: BookInterface[]) => void
  setCreateChroniqueurResponse: (response: CreateChroniqueurResponse) => void
}

const NewChroniqueur = ({
  title,
  onValidatePreSelectedAudience,
  booksSelected,
  setBooksSelected,
  setCreateChroniqueurResponse,
}: Props) => {
  const { t } = useTranslation()

  const [inputValue, setInputValue] = useState<BookSearchInterface[] | null>(
    null,
  )
  const [showImportModal, setShowImportModal] = useState(false)
  const [showImportedTable, setShowImportedTable] = useState(false)
  const [chroniqueurPreviewData, setChroniqueurPreviewData] =
    useState<ChroniqueurResponseInterface | null>(null)

  const [loadingPreviewImport, setLoadingPreviewImport] = useState(false)
  const [loadingValideImport, setLoadingValideImport] = useState(false)

  const {
    bookSelectedEan,
    setBookSelectedEan,
    bookDetails,
    isLoadingBookDetails,
    bookEditions,
    isLoadingEditions,
    bookB2BInfos,
    isLoadingB2BInfos,
    bookReco,
    isLoadingReco,
  } = useShowWorkProfileHook()

  const handleOnClickItemMenu = useCallback(
    (books: BookSearchInterface[] | null, booksToRemove?: BookInterface[]) => {
      setInputValue(books)

      if (booksToRemove) {
        const newBooksSelected = booksSelected.filter(
          (book) => !booksToRemove.includes(book),
        )

        setBooksSelected(newBooksSelected)
      }
      if (books && setBookSelectedEan) {
        setBookSelectedEan(books[0]?.id_ean || null)
      }
    },
    [setBookSelectedEan],
  )

  const handleUpdateBooksSelected = (books: BookInterface[]) => {
    setBooksSelected(books)
  }

  const handleReset = () => {
    setInputValue(null)
    setBooksSelected([])
    setShowImportedTable(false)
  }

  const handleImportAudience = () => {
    setShowImportModal(true)
  }

  const handleImportComplete = (csvData: any) => {
    if (!inputValue || !inputValue[0]?.id_ean) {
      toast.error(t('chroniqueurView.errorPreviewChronique'))
      return
    }
    setLoadingPreviewImport(true)
    PostChroniqueurPreview(csvData.file, inputValue[0].id_ean.toString())
      .then((response: any) => {
        const data = (response.data || []) as ChroniqueurResponseInterface
        setShowImportedTable(true)
        setShowImportModal(false)
        setChroniqueurPreviewData(data)
      })
      .catch(() => {
        toast.error(t('chroniqueurView.errorPreviewChronique'))
      })
      .finally(() => {
        setLoadingPreviewImport(false)
      })
  }

  const handleValidateImport = (data: InfluencerBody[]) => {
    setLoadingValideImport(true)
    PostChroniqueur(data, booksSelected[0]?.id_ean.toString() || '')
      .then((response: any) => {
        const res = (response.data || {}) as CreateChroniqueurResponse
        setCreateChroniqueurResponse(res)
        onValidatePreSelectedAudience()
      })
      .catch(() => {
        toast.error(t('chroniqueurView.errorCreatingChronique'))
      })
      .finally(() => {
        setLoadingValideImport(false)
      })
  }

  return (
    <div className="flex-auto flex flex-row gap-2">
      <div className="flex-auto flex flex-col px-8 py-4 gap-4">
        <div
          className="flex-auto flex flex-col bg-white rounded-xl p-8 gap-8"
          style={{
            boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.05)',
          }}
        >
          <ChroniquerProgressBar currentStep={1} completedSteps={[]} />
          {/* Header with title and reset button */}
          <div className="flex justify-between items-start">
            <h1 className="text-2xl font-semibold text-gray-900 font-serif">
              {title}
            </h1>
            <Button variant="outline" onClick={handleReset} className="text-sm">
              {t('reset')}
            </Button>
          </div>

          {/* Title Input Section */}
          <div className="flex-auto w-full flex flex-col gap-4 p-8 flex-1">
            <TitleInputAutoCompletion
              disable={false}
              color={SetColorConfig[0].color1}
              inputValue={inputValue}
              handleOnClickItemMenu={handleOnClickItemMenu}
              booksSelected={booksSelected}
              handleUpdateBooksSelected={handleUpdateBooksSelected}
              handleOpenBookDetails={setBookSelectedEan || (() => {})}
              eanSelected={bookSelectedEan || null}
              canSelectNotRecommendable
              addInput={() => {}}
              removeInput={() => {}}
            />
          </div>

          {/* Import Audience Section */}
          <div className="flex justify-center pt-8">
            <Button
              onClick={handleImportAudience}
              className={`flex items-center gap-2 ${
                inputValue
                  ? 'bg-black text-white hover:bg-gray-800'
                  : 'bg-white text-black border border-gray-300 hover:bg-gray-100'
              }`}
              disabled={booksSelected.length !== 1}
            >
              <Upload className="h-4 w-4" />
              {t('chroniqueurView.importAudience')}
            </Button>
          </div>
        </div>

        {/* Import Modal */}
        <ImportAudienceModal
          open={showImportModal}
          loading={loadingPreviewImport}
          onClose={() => setShowImportModal(false)}
          onImport={handleImportComplete}
        />

        {/* Imported Data Table */}
        {showImportedTable && (
          <ImportedAudienceTable
            data={
              chroniqueurPreviewData ? chroniqueurPreviewData.influencers : []
            }
            onClose={() => setShowImportedTable(false)}
            onValidate={handleValidateImport}
            loading={loadingValideImport}
          />
        )}
      </div>
      {bookDetails && (
        <WorkMirrorBookDetailsCard
          bookDetails={bookDetails}
          isLoadingGetBookDetails={isLoadingBookDetails}
          bookDetailsSelectedEditions={bookEditions}
          isLoadingGetEditions={isLoadingEditions}
          bookDetailsSelectedB2BInfo={bookB2BInfos}
          isLoadingGetB2BBookDetails={isLoadingB2BInfos}
          bookDetailsBookReco={bookReco}
          isLoadingBookDetailsGetBookReco={isLoadingReco}
          handleCloseBookDetails={() => setBookSelectedEan(null)}
          openListCard={false}
        />
      )}
    </div>
  )
}

export default memo(NewChroniqueur)
